import React, { useMemo } from "react";
import styled from "styled-components";
import { OUTSOURCE_ORDER_STATUS } from "../../../../data/enums";
import { OutsourceOrderFilterType, valueToText } from "../../../../configs/filterTypes";
import { color } from "../../../../shared/styles";

export interface StatusProgressProps {
    status?: OUTSOURCE_ORDER_STATUS | string | null;
    className?: string;
}

const StatusProgress: React.FC<StatusProgressProps> = ({ status, className }) => {
    // Define the ordered list of statuses for the progress bar
    const outsourceOrderStatuses: OUTSOURCE_ORDER_STATUS[] = useMemo(
        () => [
            OUTSOURCE_ORDER_STATUS.OPEN,
            OUTSOURCE_ORDER_STATUS.EXPORTING,
            OUTSOURCE_ORDER_STATUS.PARTIALLY_RETURNED,
            OUTSOURCE_ORDER_STATUS.COMPLETED,
        ],
        [],
    );

    const normalizedStatus = (status || "").toString() as OUTSOURCE_ORDER_STATUS;
    const isCancelled = normalizedStatus === OUTSOURCE_ORDER_STATUS.CANCELLED;
    const isCompleted = normalizedStatus === OUTSOURCE_ORDER_STATUS.COMPLETED;

    const currentIndex = useMemo(
        () => outsourceOrderStatuses.indexOf(normalizedStatus),
        [normalizedStatus, outsourceOrderStatuses],
    )

    const labels = useMemo(
        () => outsourceOrderStatuses.map((s) => valueToText(OutsourceOrderFilterType, "status", s) || s),
        [outsourceOrderStatuses],
    );

    const getStepState = (i: number): ProgressStepState => {
        if (isCompleted) return "completed";
        if (isCancelled) return "cancelled";
        if (currentIndex < 0) return "idle";
        if (i < currentIndex) return "completed";
        if (i === currentIndex) return "active";
        return "idle";
    };

    return (
        <ProgressWrapper className={className}>
            <ProgressTrack>
                {outsourceOrderStatuses.map((_, i) => (
                    <ProgressStep key={i}>
                        <ProgressSegment $state={getStepState(i)} aria-hidden />
                        <ProgressLabel $state={getStepState(i)}>
                            {labels[i]}
                        </ProgressLabel>
                    </ProgressStep>
                ))}
            </ProgressTrack>
        </ProgressWrapper>
    );
};

export default StatusProgress;

type ProgressStepState = "completed" | "active" | "cancelled" | "idle";

const getSegmentColor = (state: ProgressStepState) => {
    switch (state) {
        case "completed":
            return color.status.SUCCESS;
        case "active":
            return color.status.WARNING;
        case "cancelled":
            return color.status.FAIL;
        default:
            return color.secondary.BORDER;
    }
};

const getLabelColor = (state: ProgressStepState) => {
    switch (state) {
        case "completed":
        case "active":
            return "#111111";
        case "cancelled":
            return color.status.FAIL;
        default:
            return color.emphasis.NEUTRAL_N70;
    }
};

const ProgressWrapper = styled.div`
    width: 100%;
`;

const ProgressTrack = styled.div`
    width: 100%;
    display: flex;
    gap: 4px;
`;

const ProgressStep = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

const ProgressSegment = styled.div<{ $state: ProgressStepState }>`
    height: 8px;
    border-radius: 4px;
    background-color: ${({ $state }) => getSegmentColor($state)};
`;

const ProgressLabel = styled.div<{ $state: ProgressStepState }>`
    margin-top: 8px;
    text-align: center;
    font-size: 12px;
    font-weight: ${({ $state }) => ($state === "idle" ? 500 : 700)};
    line-height: 16px;
    color: ${({ $state }) => getLabelColor($state)};
`;
